/**
 * LandingPage Component - HI-SI Brand Landing Page
 * HISIP-016: Landing + Demo (Brand Site for HI‑SI)
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useServerAuth } from '@/hooks/useAuth-cookie';

// Landing components
import Hero from '@/components/landing/Hero';
import DemoPortal from '@/components/landing/DemoPortal';
import Features from '@/components/landing/Features';
import PricingCTA from '@/components/landing/PricingCTA';
import Footer from '@/components/landing/Footer';

// Futuristic components for consistent styling
import AtmosphericBackground from '@/components/futuristic/AtmosphericBackground';

interface LandingPageProps {
  className?: string;
}

export default function LandingPage({ className }: LandingPageProps) {
  const navigate = useNavigate();
  const [, setShowDemo] = useState(false);
  const { user, session } = useServerAuth();

  // HISI-FE-AUTH-01: Cookie-based auth, no token handling needed

  // If already authenticated, forward to dashboard
  useEffect(() => {
    if (user && session) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, session, navigate]);

  // Analytics tracking function (basic implementation)
  const trackAnalytics = (event: string, data?: any) => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', event, {
        event_category: 'HI-SI Landing',
        event_label: 'CTA Click',
        ...data
      });
    }
    console.log('Analytics:', event, data);
  };

  // Navigation handlers
  const handleStartCreating = () => {
    trackAnalytics('start_creating_click', { 
      source: 'hero_primary_cta',
      timestamp: new Date().toISOString()
    });
    navigate('/signup?utm_source=landing&utm_medium=cta&utm_campaign=hero');
  };

  const handleWatchDemo = () => {
    trackAnalytics('watch_demo_click', { 
      source: 'hero_secondary_cta',
      timestamp: new Date().toISOString()
    });
    setShowDemo(true);
    // Scroll to demo section
    document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSeePricing = () => {
    trackAnalytics('see_pricing_click', { 
      source: 'pricing_cta',
      timestamp: new Date().toISOString()
    });
    // Scroll to pricing section
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleLogin = () => {
    trackAnalytics('login_click', { 
      source: 'header_login',
      timestamp: new Date().toISOString()
    });
    navigate('/login?utm_source=landing');
  };

  // Legal/support handlers
  const handleTerms = () => {
    trackAnalytics('terms_click');
    window.open('/terms', '_blank');
  };

  const handlePrivacy = () => {
    trackAnalytics('privacy_click');
    window.open('/privacy', '_blank');
  };

  const handleContact = () => {
    trackAnalytics('contact_click');
    window.open('/contact', '_blank');
  };

  // Smooth scrolling for anchor links
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      console.log('🔗 LandingPage hashchange:', hash);

      // Skip if hash contains query parameters or is not a valid CSS selector
      if (!hash || hash.includes('=') || hash.includes('&')) {
        console.log('🔗 Skipping hash navigation (invalid selector):', hash);
        return;
      }

      // Only try querySelector if hash looks like a valid CSS selector (starts with #)
      try {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } catch (error) {
        console.warn('🔗 Invalid CSS selector in hash:', hash, error);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Hi-Si.ai — AI Video Creation for the Age of On‑Demand Media</title>
        <meta 
          name="description" 
          content="Transform ideas into professional videos through our eleven-stage AI pipeline. From script to screen in minutes, not hours. Managed by your personal Agent."
        />
        <meta 
          name="keywords" 
          content="AI video creation, automated video production, AI content creation, video generator, professional video, AI pipeline"
        />
        
        {/* Open Graph */}
        <meta property="og:title" content="Hi-Si.ai — AI Video Creation for the Age of On‑Demand Media" />
        <meta property="og:description" content="Transform ideas into professional videos through our eleven-stage AI pipeline. Managed by your personal Agent." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://hi-si.ai" />
        <meta property="og:image" content="https://hi-si.ai/og-image.jpg" />
        <meta property="og:site_name" content="HI-SI" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@hisi_ai" />
        <meta name="twitter:title" content="Hi-Si.ai — AI Video Creation" />
        <meta name="twitter:description" content="Transform ideas into professional videos through our eleven-stage AI pipeline." />
        <meta name="twitter:image" content="https://hi-si.ai/twitter-image.jpg" />
        
        {/* Canonical URL - Primary domain per HISIP-016 */}
        <link rel="canonical" href="https://hi-si.ai/" />
        
        {/* Structured Data - JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "HI-SI",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "description": "AI-powered video creation platform with seven-stage pipeline for professional content generation",
            "url": "https://hi-si.ai",
            "screenshot": "https://hi-si.ai/og-image.jpg",
            "offers": {
              "@type": "Offer",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            },
            "publisher": {
              "@type": "Organization",
              "name": "HI-SI",
              "url": "https://hi-si.ai"
            }
          })}
        </script>
        
        {/* Preconnect for performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        
        {/* Favicon */}
        <link rel="icon" type="image/x-icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        
        {/* Theme color for mobile browsers */}
        <meta name="theme-color" content="#00A6FB" />
        <meta name="msapplication-TileColor" content="#00A6FB" />
        
        {/* Additional meta for security */}
        <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
        {/* X-Frame-Options must be set via HTTP header; meta is ignored and logs a warning */}
        <meta httpEquiv="X-XSS-Protection" content="1; mode=block" />
      </Helmet>

      <div className={`min-h-screen bg-black text-white relative overflow-x-hidden ${className}`}>
        {/* Global Atmospheric Background */}
        <div className="fixed inset-0 z-0">
          <AtmosphericBackground />
        </div>

        {/* Navigation Bar */}
        <motion.nav
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-40 px-4 sm:px-6 lg:px-8 py-6"
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-cyan-500/20 border border-purple-500/30 flex items-center justify-center">
                <span className="text-purple-400 font-bold text-lg">H</span>
              </div>
              <div>
                <div className="text-xl font-light">
                  <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                    HI-SI
                  </span>
                </div>
              </div>
            </div>

            {/* Navigation Links - Hidden on mobile */}
            <div className="hidden md:flex items-center gap-8">
              <a 
                href="#features" 
                className="text-white/70 hover:text-white transition-colors text-sm"
                onClick={() => trackAnalytics('nav_features_click')}
              >
                Features
              </a>
              <a 
                href="#pricing" 
                className="text-white/70 hover:text-white transition-colors text-sm"
                onClick={() => trackAnalytics('nav_pricing_click')}
              >
                Pricing
              </a>
              {user && session ? (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="text-white/70 hover:text-white transition-colors text-sm"
                >
                  Dashboard
                </button>
              ) : (
                <button
                  onClick={handleLogin}
                  className="text-white/70 hover:text-white transition-colors text-sm"
                >
                  Log In
                </button>
              )}
              <motion.button
                onClick={handleStartCreating}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-cyan-600 text-white text-sm font-medium hover:shadow-lg hover:shadow-purple-500/25 transition-all"
              >
                Start Creating
              </motion.button>
            </div>

            {/* Mobile CTA */}
            <div className="md:hidden flex items-center gap-4">
              {user && session ? (
                <motion.button
                  onClick={() => navigate('/dashboard')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-4 py-2 rounded-lg bg-white/10 text-white text-sm font-medium border border-white/20"
                >
                  Dashboard
                </motion.button>
              ) : (
                <motion.button
                  onClick={handleLogin}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-4 py-2 rounded-lg bg-white/10 text-white text-sm font-medium border border-white/20"
                >
                  Log In
                </motion.button>
              )}
              <motion.button
                onClick={handleStartCreating}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-cyan-600 text-white text-sm font-medium"
              >
                Start
              </motion.button>
            </div>
          </div>
        </motion.nav>

        {/* Main Content */}
        <main className="relative z-10">
          {/* Hero Section */}
          <section id="hero">
            <Hero 
              onStartCreating={handleStartCreating}
              onWatchDemo={handleWatchDemo}
              onSeePricing={handleSeePricing}
            />
          </section>

          {/* Demo Section */}
          <section id="demo" className="py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-12"
              >
                <h2 className="text-4xl sm:text-5xl font-light text-white mb-6">
                  <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                    See It In Action
                  </span>
                </h2>
                <p className="text-xl text-white/70 max-w-3xl mx-auto">
                  Experience our eleven-stage AI pipeline in real-time. 
                  Watch as your ideas transform into professional videos.
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                className="max-w-5xl mx-auto"
              >
                <DemoPortal />
              </motion.div>
            </div>
          </section>

          {/* Features Section */}
          <section id="features">
            <Features />
          </section>

          {/* Pricing Section */}
          <PricingCTA 
            onStartCreating={handleStartCreating}
            onSeePricing={handleSeePricing}
          />
        </main>

        {/* Footer */}
        <Footer 
          onTerms={handleTerms}
          onPrivacy={handlePrivacy}
          onContact={handleContact}
        />

        {/* Scroll to Top Button */}
        <motion.button
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2 }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full bg-black/50 backdrop-blur-xl border border-purple-500/30 flex items-center justify-center hover:bg-black/70 transition-all hover:scale-110"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </motion.button>
      </div>
    </>
  );
}